import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import introJs from 'intro.js';
import 'intro.js/introjs.css';
import { useOnboardingContext } from '../../contexts/OnboardingContext';

const OnboardingTour = () => {
  const navigate = useNavigate();
  const {
    isTourActive,
    tourStep,
    pendingTourStart,
    startTour,
    completeTour,
    skipTour,
    setCurrentStep,
  } = useOnboardingContext();

  const introRef = useRef(null);
  const startedRef = useRef(false);
  const callbacksRef = useRef({ completeTour, skipTour, setCurrentStep });
  callbacksRef.current = { completeTour, skipTour, setCurrentStep };

  // Start tour when requested (on any page); redirect to dashboard only after "Start Tour" is pressed
  useEffect(() => {
    if (pendingTourStart) {
      startTour();
    }
  }, [pendingTourStart, startTour]);

  useEffect(() => {
    if (!isTourActive) return;

    if (introRef.current) {
      introRef.current.exit(true);
      introRef.current = null;
    }
    startedRef.current = false;

    const intro = introJs.tour(document.body);

    intro.setOptions({
      steps: [
        {
          intro: `
            <div class="text-center">
              <h3 class="text-lg font-bold mb-2">Welcome to MoonReady!</h3>
              <p class="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Your crypto portfolio command center. Let's take a quick tour to get you started.
              </p>
              <p class="text-sm text-gray-600 dark:text-gray-300 mb-2 font-medium">In about 3 minutes, you'll:</p>
              <ul class="text-left inline-block space-y-2 text-sm text-gray-600 dark:text-gray-300 mb-4">
                <li class="flex items-center gap-2"><span class="text-green-500">✓</span> Add your first test transactions</li>
                <li class="flex items-center gap-2"><span class="text-green-500">✓</span> See how PnL calculations work</li>
                <li class="flex items-center gap-2"><span class="text-green-500">✓</span> Set up an exit strategy</li>
              </ul>
              <p class="text-sm text-gray-500 dark:text-gray-400">Click <strong>Start Tour</strong> to begin, or <strong>No thanks</strong> to close.</p>
            </div>
          `,
          position: 'floating',
        },
        {
          intro: `
            <div class="text-center">
              <h3 class="text-lg font-bold mb-2">Welcome to your Dashboard</h3>
              <p class="text-sm text-gray-600 dark:text-gray-300">
                This is your command center. Here you'll see your portfolio value,
                profit/loss, and how your investments are performing at a glance.
              </p>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-3">
                Right now it's empty — let's add some test data to see it in action!
              </p>
            </div>
          `,
          position: 'floating',
        },
      ],
      showProgress: false,
      showBullets: false,
      showStepNumbers: false,
      exitOnOverlayClick: false,
      exitOnEsc: true,
      nextLabel: 'Start Tour',
      prevLabel: '← Back',
      skipLabel: 'No thanks',
      doneLabel: 'Got it!',
      hidePrev: true,
      scrollToElement: true,
      scrollPadding: 80,
      overlayOpacity: 0.5,
      tooltipRenderAsHtml: true,
      tooltipClass: 'onboarding-tooltip',
      highlightClass: 'onboarding-highlight',
    });

    intro.oncomplete(() => {
      callbacksRef.current.completeTour();
    });

    intro.onexit(() => {
      callbacksRef.current.skipTour();
    });

    const updateTooltipStep = (introInstance) => {
      const step = (typeof introInstance.getCurrentStep === 'function' ? introInstance.getCurrentStep() : undefined) ?? introInstance._currentStep;
      const tooltip = document.querySelector('.introjs-tooltip');
      if (tooltip) tooltip.setAttribute('data-current-step', String(step ?? 0));
    };

    intro.onbeforechange(function () {
      const current = (typeof this.getCurrentStep === 'function' ? this.getCurrentStep() : undefined) ?? this._currentStep;
      if (current != null) callbacksRef.current.setCurrentStep(current);
      if (current === 0) {
        navigate('/');
      }
      setTimeout(() => updateTooltipStep(this), 0);
    });

    introRef.current = intro;

    const t = setTimeout(() => {
      startedRef.current = true;
      intro.start();
      if (tourStep > 0) {
        try { intro.goToStep(tourStep + 1); } catch (_) {}
      }
      setTimeout(() => updateTooltipStep(intro), 0);
    }, 0);

    return () => {
      clearTimeout(t);
      if (introRef.current && !startedRef.current) {
        introRef.current.exit(true);
      }
      introRef.current = null;
    };
  }, [isTourActive, tourStep, navigate]);

  useEffect(() => {
    return () => {
      if (introRef.current) {
        introRef.current.exit(true);
      }
    };
  }, []);

  return null;
};

export default OnboardingTour;
